// Send a text or email (gap #3, office-history's "Text <customer>"): to one of the customer's phones
// or emails, with its consent said beside it, through messages.send. A refusal (no consent,
// do-not-contact) is shown in the api's own words; a company that can't text yet is told so first.
// In Messages it sits beside the list; on a customer's page it opens as a sheet (SendMessageSheet).

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import type { Customer } from '../types.ts';
import { BODY_MAX, reachable } from './messages-model.ts';
import './messages.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function SendMessage({
  customerId,
  to,
  onSent,
  onCancel,
  bare = false,
}: {
  customerId: string;
  /** Start on this address (a reply goes back where the message went). */
  to?: string;
  onSent: (what: string) => void;
  onCancel: () => void;
  /** Inside a sheet: no stone of its own. */
  bare?: boolean;
}) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [sms, setSms] = useState<{ ok: boolean; notSetUp: string } | null>(null);
  const [methodId, setMethodId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<Customer>('customers.get', { id: customerId })
      .then((c) => {
        setCustomer(c);
        const ways = reachable(c);
        const start =
          ways.find((w) => to && w.method.value === to && w.sendable) ??
          ways.find((w) => w.sendable) ??
          ways[0];
        setMethodId(start?.method.id ?? null);
      })
      .catch((e: unknown) => setError(message(e)));
    client
      .call<{ sms: boolean; notSetUp: string }>('messages.channels', {})
      .then((r) => setSms({ ok: r.sms, notSetUp: r.notSetUp }))
      .catch(() => setSms(null));
  }, [customerId, to]);

  const ways = customer ? reachable(customer) : [];
  const way = ways.find((w) => w.method.id === methodId) ?? null;
  const email = way?.method.kind === 'email';
  const noTexting = !!way && !email && sms !== null && !sms.ok;
  const missing = !way
    ? 'Pick where it goes.'
    : !way.sendable
      ? way.chip.text === 'Do not contact'
        ? 'This customer asked not to be contacted.'
        : `${way.contactName} hasn’t agreed to ${email ? 'email' : 'texts'} at this address.`
      : noTexting
        ? sms!.notSetUp
        : email && !subject.trim()
          ? 'Add a subject.'
          : !body.trim()
            ? 'Write the message.'
            : null;

  const send = async () => {
    if (!way || missing) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('messages.send', {
        contactMethodId: way.method.id,
        purpose: 'general',
        body: body.trim(),
        ...(email ? { subject: subject.trim() } : {}),
      });
      onSent(`${email ? 'Email' : 'Text'} to ${way.contactName} is on its way.`);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className={`${bare ? '' : 'stone '}msg-send`}
      aria-label={customer ? `Write to ${customer.displayName}` : 'Write a message'}
    >
      <h3>{customer ? `Write to ${customer.displayName}` : 'Write a message'}</h3>
      {!customer && !error && <p className="muted small">Getting their phones and emails.</p>}
      {customer && !ways.length && (
        <p className="muted">There’s no phone or email on this customer yet. Add one under Contacts first.</p>
      )}
      {ways.length > 0 && (
        <fieldset className="msg-to">
          <legend>To</legend>
          {ways.map((w) => (
            <label key={w.method.id} className="checkrow msg-way">
              <input
                type="radio"
                name="msg-to"
                checked={w.method.id === methodId}
                onChange={() => setMethodId(w.method.id)}
              />
              <span className="grow">
                {w.contactName} · <span className="nowrap">{w.address}</span>
              </span>
              <span className={`chip say ${w.chip.tone}`}>{w.chip.text}</span>
            </label>
          ))}
        </fieldset>
      )}
      {email && (
        <label className="f">
          Subject
          <input
            className="field"
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
      )}
      {way && (
        <label className="f">
          Message
          <textarea
            className="field"
            rows={4}
            value={body}
            maxLength={BODY_MAX}
            onChange={(e) => setBody(e.target.value)}
            aria-describedby="msg-count msg-missing"
          />
          <span className="small muted" id="msg-count">
            {body.length} of {BODY_MAX.toLocaleString('en-CA')}
          </span>
        </label>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn primary" disabled={busy || !!missing} onClick={() => void send()}>
          {busy ? 'Sending…' : email ? 'Send email' : 'Send text'}
        </button>
        <button className="btn quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <p className="small muted" id="msg-missing" role="status" aria-live="polite">
        {missing && !busy && customer ? missing : ''}
      </p>
    </section>
  );
}

/** "Send a text or email" on a customer's page: the same composer, as a sheet. */
export function SendMessageSheet({
  customerId,
  customerName,
  onSent,
  onClose,
}: {
  customerId: string;
  customerName: string;
  onSent: (what: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="sheet ask messages msg-sheet"
      aria-label={`Write to ${customerName}`}
      onClose={onClose}
    >
      <SendMessage customerId={customerId} onSent={onSent} onCancel={onClose} bare />
    </dialog>
  );
}
